// How the app LOOKS, per person: text size, how bright the words are, the accent colour, the background.
//
// ⚠ ASKED FOR (2026-09-17, after squinting at the haggle card): "we really should have an option under the user's Account &
// Settings to adjust the font size, colors, background color for better viewability for each user." Everyone at the table
// has different eyes and a different screen — a GM on a 27" monitor, a player on a phone in a dim room.
//
// ⚠ HOW IT WORKS. The whole stylesheet is in rem and reads its colours from a dozen tokens on :root (styles.css), so a
// preference is a handful of inline custom properties on <html> — no second stylesheet, nothing per component. It is kept
// in TWO places: this device (localStorage — applied before the first paint, and it works on a shared-link screen with no
// account) and the account (`user_settings.prefs.display` — it follows the person to their next device; the account's copy
// wins when it exists).
//
// ⚠ EVERY ACCENT HAS ITS OWN DARK INK. The owner's standing rule is "never light text on a gold background"; an accent
// here is always a LIGHT colour paired with a near-black ink chosen for it, so a pressed button stays readable in any of them.
// ⚠ LIGHT GROUNDS (2026-09-17, "a light background theme"). The sheet painted ~170 colours by hand — solid dark slabs under
// notices and toasts, cream text, status greens and reds, gold used as TEXT. Every one is now `var(--t-…, <the old colour>)`
// (scratch script light-css.py; a round-trip proved the dark sheet byte-identical). The --t- tokens are defined NOWHERE in
// the stylesheet, so a dark ground falls through to exactly what it always was; a light ground defines them here.
// On a light ground gold cannot be TEXT (2:1 on parchment), so each accent carries a dark twin, `text`, for words and lines;
// the FILL of a pressed button stays the light accent with its dark ink — never light text on gold, in either world.
import { supabase } from "./supabase";

export type TextSize = 90 | 100 | 112 | 125 | 140;
/** `dice` is a dice-style id (lib/dice-style.ts); it sets no CSS — the 3D dice layer reads it. Kept here so it rides the
 * same per-account prefs as the other colours, and works on a share-link screen from localStorage with no account. */
export type DisplayPrefs = { text: TextSize; bright: boolean; accent: string; ground: string; dice: string };

export const DEFAULT_DISPLAY: DisplayPrefs = { text: 100, bright: false, accent: "gold", ground: "study", dice: "brass" };

export const TEXT_SIZES: readonly { id: TextSize; label: string }[] = [
  { id: 90, label: "Small" }, { id: 100, label: "Normal" }, { id: 112, label: "Large" }, { id: 125, label: "Larger" }, { id: 140, label: "Largest" },
];

export const ACCENTS: readonly { id: string; label: string; accent: string; rgb: string; ink: string; /** The accent as words and lines on a LIGHT ground. */ text: string }[] = [
  { id: "gold", label: "Gold", accent: "#d1a24a", rgb: "209, 162, 74", ink: "#201808", text: "#66470b" },
  { id: "amber", label: "Amber", accent: "#e8923f", rgb: "232, 146, 63", ink: "#221004", text: "#763d07" },
  { id: "rose", label: "Rose", accent: "#e88aa2", rgb: "232, 138, 162", ink: "#2a0a14", text: "#8d2748" },
  { id: "teal", label: "Teal", accent: "#55c2b0", rgb: "85, 194, 176", ink: "#04201b", text: "#0a574b" },
  { id: "sky", label: "Sky", accent: "#74b6ee", rgb: "116, 182, 238", ink: "#06182a", text: "#185083" },
  { id: "silver", label: "Silver", accent: "#cfcfcf", rgb: "207, 207, 207", ink: "#151515", text: "#474747" },
];

export type Ground = { id: string; label: string; bg: string; panel: string; panelRgb: string; void: string; voidRgb: string; line: string;
  /** A LIGHT ground: dark words, and the --t- tokens below. */
  light?: { ink: string; muted: string; brightInk: string; brightMuted: string; slab: string; well: string; tintRgb: string;
    /**
     * ⚠ THE TWO THINGS A LIGHT GROUND CANNOT INHERIT FROM THE DARK ONE (2026-09-17, measured in the harness).
     * `shadeRgb`: an inset well shades itself `rgba(<this>, α)`. Black at 0.2 turned a #faf4e4 card into #c8c3b6
     * and put 45 readable words on one screen under the line at once; a warm mid-tone keeps the well a shade of
     * the page. `press`/`pressEdge`: a pressed button's deep gold was fixed while its words flipped dark with the
     * ground — dark on dark gold, 3.05 to 1. The fill has to lighten when the words darken. */
    shadeRgb: string; press: string; pressEdge: string; glyph: string } };

/**
 * Status words on a light ground.
 *
 * ⚠ EVERY ONE OF THESE IS DERIVED, NOT PICKED (2026-09-17). A word does not sit on the page — it sits on whatever
 * surface it is in, and the deepest one here is an inset well over the void: #cdbe9a on Parchment, #cdcdc9 on
 * Daylight. These are each darkened until they clear 4.5 to 1 on THAT, which is what makes them safe everywhere
 * lighter. Same for the accent inks below and each ground's `muted`. Re-derive with the worst surface, never by eye. */
const LIGHT_STATUS = { "--t-good": "#165932", "--t-bad": "#922323", "--t-warn": "#6b4600", "--t-magic": "#6a2fa0", "--danger": "#922323", "--t-ink-rgb": "43, 34, 20" };

export const GROUNDS: readonly Ground[] = [
  { id: "study", label: "Study (warm)", bg: "#15130f", panel: "#1e1a15", panelRgb: "30, 26, 21", void: "#0c0a07", voidRgb: "12, 10, 7", line: "#332c22" },
  { id: "black", label: "True black", bg: "#000000", panel: "#0e0e0e", panelRgb: "14, 14, 14", void: "#000000", voidRgb: "0, 0, 0", line: "#3a3a3a" },
  { id: "slate", label: "Slate", bg: "#10141a", panel: "#182029", panelRgb: "24, 32, 41", void: "#090c10", voidRgb: "9, 12, 16", line: "#2c3948" },
  { id: "forest", label: "Forest", bg: "#0f1410", panel: "#172019", panelRgb: "23, 32, 25", void: "#080c09", voidRgb: "8, 12, 9", line: "#2a3a2d" },
  { id: "wine", label: "Wine", bg: "#170f12", panel: "#21161a", panelRgb: "33, 22, 26", void: "#0d0709", voidRgb: "13, 7, 9", line: "#3d2932" },
  { id: "parchment", label: "Parchment (light)", bg: "#eee4cc", panel: "#faf4e4", panelRgb: "250, 244, 228", void: "#e2d6b8", voidRgb: "226, 214, 184", line: "#c9b993",
    light: { ink: "#2b2214", muted: "#574b36", brightInk: "#140e04", brightMuted: "#3a2f1f", slab: "#fffaf0", well: "#ded2b8", tintRgb: "250, 244, 228",
      shadeRgb: "150, 129, 78", press: "#e6c689", pressEdge: "#b08b3c", glyph: "rgba(102, 71, 11, 0.8)" } },
  { id: "daylight", label: "Daylight (light)", bg: "#f1f1ee", panel: "#ffffff", panelRgb: "255, 255, 255", void: "#e3e3df", voidRgb: "227, 227, 223", line: "#c8c8c2",
    light: { ink: "#1f1f1f", muted: "#575755", brightInk: "#000000", brightMuted: "#333332", slab: "#ffffff", well: "#d5d5d0", tintRgb: "255, 255, 255",
      shadeRgb: "150, 150, 145", press: "#e8cf9b", pressEdge: "#b3944d", glyph: "rgba(102, 71, 11, 0.8)" } },
];

/** A tolerant read: anything unknown falls back to the default for THAT setting, never the lot. */
export function asDisplay(v: unknown): DisplayPrefs {
  const o = (v && typeof v === "object" ? v : {}) as Record<string, unknown>;
  return {
    text: TEXT_SIZES.some((t) => t.id === o.text) ? (o.text as TextSize) : DEFAULT_DISPLAY.text,
    bright: o.bright === true,
    accent: ACCENTS.some((a) => a.id === o.accent) ? (o.accent as string) : DEFAULT_DISPLAY.accent,
    ground: GROUNDS.some((g) => g.id === o.ground) ? (o.ground as string) : DEFAULT_DISPLAY.ground,
    // The dice layer tolerates an unknown id (falls back to the first style), so keep any string rather than dropping it.
    dice: typeof o.dice === "string" ? o.dice : DEFAULT_DISPLAY.dice,
  };
}

/** The custom properties a preference sets on <html>. A default sets NOTHING for its part — the stylesheet's own values stand. */
export function displayVars(p: DisplayPrefs): Record<string, string> {
  const out: Record<string, string> = {};
  const a = ACCENTS.find((x) => x.id === p.accent), g = GROUNDS.find((x) => x.id === p.ground);
  if (a && a.id !== DEFAULT_DISPLAY.accent) Object.assign(out, { "--accent": a.accent, "--accent-rgb": a.rgb, "--accent-ink": a.ink });
  if (g && g.id !== DEFAULT_DISPLAY.ground) Object.assign(out, { "--bg": g.bg, "--panel": g.panel, "--panel-rgb": g.panelRgb, "--void": g.void, "--void-rgb": g.voidRgb, "--line": g.line });
  if (g?.light) {
    const L = g.light;
    Object.assign(out, {
      "--ink": p.bright ? L.brightInk : L.ink, "--muted": p.bright ? L.brightMuted : L.muted, "--accent-text": (a ?? ACCENTS[0]!).text,
      "--t-ink": p.bright ? L.brightInk : L.ink, "--t-slab": L.slab, "--t-well": L.well, "--t-tint-rgb": L.tintRgb,
      "--t-shade-rgb": L.shadeRgb, "--t-press": L.press, "--t-press-edge": L.pressEdge, "--t-glyph": L.glyph, ...LIGHT_STATUS,
    });
  } else if (p.bright) Object.assign(out, { "--ink": "#fffaf0", "--muted": "#d8ccb6" });
  return out;
}

const ALL_VARS = ["--accent", "--accent-rgb", "--accent-ink", "--accent-text", "--bg", "--panel", "--panel-rgb", "--void", "--void-rgb", "--line", "--ink", "--muted",
  "--t-ink", "--t-slab", "--t-well", "--t-tint-rgb", "--t-shade-rgb", "--t-press", "--t-press-edge", "--t-glyph",
  "--t-good", "--t-bad", "--t-warn", "--t-magic", "--danger", "--t-ink-rgb"];
const KEY = "vttmaps.display";

export function applyDisplay(p: DisplayPrefs, root: HTMLElement = document.documentElement): void {
  const vars = displayVars(p);
  for (const name of ALL_VARS) { if (name in vars) root.style.setProperty(name, vars[name]!); else root.style.removeProperty(name); }
  // rem is the unit of the whole sheet: one number scales every word, box and button together.
  if (p.text === 100) root.style.removeProperty("font-size"); else root.style.fontSize = `${p.text}%`;
  // Form controls, scrollbars and the like follow the ground; and a hook for the few rules a token cannot reach.
  const light = !!GROUNDS.find((x) => x.id === p.ground)?.light;
  root.style.colorScheme = light ? "light" : "";
  if (light) root.dataset.ground = "light"; else delete root.dataset.ground;
}

export function readDisplay(): DisplayPrefs {
  try { return asDisplay(JSON.parse(localStorage.getItem(KEY) ?? "null")); } catch { return { ...DEFAULT_DISPLAY }; }
}

function keepLocally(p: DisplayPrefs): void { try { localStorage.setItem(KEY, JSON.stringify(p)); } catch { /* it still applies until the page closes */ } }

/** Apply now, keep on this device, and — signed in — on the account. False only when the account write failed (it still applies here). */
export async function saveDisplay(p: DisplayPrefs, userId: string | null): Promise<boolean> {
  applyDisplay(p);
  keepLocally(p);
  if (!userId) return true;
  // `prefs` is one jsonb for every preference: read it, change our part, write it back.
  const { data, error: readError } = await supabase.from("user_settings").select("prefs").eq("user_id", userId).maybeSingle();
  if (readError) return false;
  const prefs = { ...(((data as { prefs?: Record<string, unknown> | null } | null)?.prefs) ?? {}), display: p };
  const { error } = await supabase.from("user_settings").upsert({ user_id: userId, prefs, updated_at: new Date().toISOString() }, { onConflict: "user_id" });
  return !error;
}

/**
 * Once, at startup (main.tsx): this device's look before the first paint, then the account's whenever somebody is signed in
 * — so a preference set on the desk machine is there on the phone at the table.
 */
export function startDisplay(): void {
  applyDisplay(readDisplay());
  const fromAccount = async (userId: string | undefined) => {
    if (!userId) return;
    const { data, error } = await supabase.from("user_settings").select("prefs").eq("user_id", userId).maybeSingle();
    const saved = (data as { prefs?: { display?: unknown } | null } | null)?.prefs?.display;
    if (error || !saved) return;
    const p = asDisplay(saved);
    applyDisplay(p);
    keepLocally(p);
  };
  try {
    void supabase.auth.getSession().then(({ data }) => fromAccount(data.session?.user.id));
    supabase.auth.onAuthStateChange((event, s) => { if (event === "SIGNED_IN") void fromAccount(s?.user.id); });
  } catch { /* not configured: this device's look stands */ }
}
